import { PHONE_DISPLAY, PHONE_TEL, TID } from "@/config/site";
import { Phone, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";

const links = [
  { href: "/", label: "Home", tid: TID.navHome, isRoute: true },
  { href: "/#services", label: "Services", tid: TID.navServices },
  { href: "/#pricing", label: "Pricing", tid: "nav-link-pricing" },
  { href: "/cities", label: "Cities", tid: TID.navAreas },
  { href: "/#reviews", label: "Reviews", tid: "nav-link-reviews" },
  { href: "/#faq", label: "FAQ", tid: "nav-link-faq" },
  { href: "/contact", label: "Contact", tid: TID.navContact, isRoute: true },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Top utility strip */}
      <div className="hidden md:block bg-black text-slate-200 text-xs">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <span className="font-display tracking-[0.28em] uppercase text-zinc-500">
            Bellevue · Kirkland · Redmond · Bothell · Woodinville · Sammamish · Issaquah · Mercer Island · Newcastle
          </span>
          <span className="flex items-center gap-2 text-yellow-400 font-semibold">
            <span className="relative inline-block h-2 w-2 rounded-full bg-green-500">
              <span className="bm-pulse absolute inset-0" />
            </span>
            OPEN 24/7 — Dispatching Now
          </span>
        </div>
      </div>

      <header
        data-testid={TID.navRoot}
        className={`sticky top-0 z-40 transition-[background-color,border-color,box-shadow] duration-300 ${
          scrolled
            ? "lux-glass-strong border-b border-yellow-400/30"
            : "bg-transparent border-b border-white/5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <a
            href="#home"
            data-testid={TID.logo}
            className="flex items-center gap-3 group"
          >
            <div className="h-12 w-12 sm:h-14 sm:w-14 bg-white border-2 border-yellow-400 p-1 flex items-center justify-center">
              <img
                src="/images/logo.png"
                alt="BM Towing & Recovery logo"
                width="56"
                height="56"
                decoding="async"
                fetchPriority="high"
                className="max-h-full max-w-full object-contain"
              />
            </div>
            <div className="leading-none">
              <div className="font-display text-xl sm:text-2xl font-black uppercase text-white tracking-tight">
                BM Towing
              </div>
              <div className="font-display text-[10px] sm:text-[11px] tracking-[0.25em] text-yellow-400 uppercase">
                & Recovery · Kirkland, WA
              </div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={l.tid}
                className="font-display uppercase tracking-widest text-sm text-slate-200 hover:text-yellow-400 px-4 py-2 border-2 border-transparent hover:border-yellow-400/30 transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid={TID.navCall}
              aria-label={`Call BM Towing & Recovery 24/7 at ${PHONE_DISPLAY}`}
              className="hidden sm:inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-5 py-2.5 rounded-full lux-glow-yellow transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-yellow-300"
            >
              <Phone className="h-5 w-5" strokeWidth={3} aria-hidden="true" />
              <span className="hidden md:inline">Call {PHONE_DISPLAY}</span>
              <span className="md:hidden">Call Now</span>
            </a>
            <button
              className="lg:hidden text-white p-2 lux-glass rounded-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-yellow-300"
              data-testid={TID.navMenuBtn}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-nav"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
            </button>
          </div>
        </div>

        {open && (
          <div id="mobile-nav" className="lg:hidden lux-glass-strong border-t border-yellow-400/30">
            <div className="px-4 py-3 flex flex-col">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`${l.tid}-mobile`}
                  onClick={() => setOpen(false)}
                  className="font-display uppercase tracking-widest text-slate-200 hover:text-yellow-400 py-3 border-b border-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid={`${TID.navCall}-mobile`}
                aria-label={`Call BM Towing & Recovery at ${PHONE_DISPLAY}`}
                className="mt-3 sm:hidden bg-yellow-400 text-black font-display font-black uppercase tracking-wider px-4 py-3 text-center rounded-full lux-glow-yellow"
              >
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
